import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  getAgentCommissionPerformance,
  type AgentCommissionPerformance,
} from '../services/commissionService';
import CommissionTimeline from '../components/CommissionTimeline';
import {
  buildExportRows,
  exportCommissionToCsv,
} from '../utils/commissionExport';
import type { CommissionStatus } from '../types/commission';

type PipelineProperty = {
  propertyId: string;
  propertyAddress: string;
  soldPrice: number;
  commission: number;
  shareAmount: number;
  soldDate: string;
  settlementDate: string;
  status: CommissionStatus;
};

const statusLabel = (s: CommissionStatus) =>
  s === 'pending' ? 'Pending' : s === 'paid' ? 'Paid' : 'Cancelled';
const statusColor = (s: CommissionStatus) =>
  s === 'paid' ? 'success' : s === 'cancelled' ? 'default' : 'warning';

function PipelineTable({
  title,
  subtitle,
  icon,
  color,
  properties,
  agencySlug,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  color: 'warning' | 'success' | 'default';
  properties: PipelineProperty[];
  agencySlug?: string;
}) {
  if (properties.length === 0) return null;
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <Box sx={{ color: `${color}.main` }}>{icon}</Box>
          <Typography variant="h6">{title}</Typography>
          <Chip size="small" label={properties.length} color={color} variant="outlined" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Property Address</TableCell>
                <TableCell align="right">Sold Price</TableCell>
                <TableCell align="right">Total Commission</TableCell>
                <TableCell align="right">My Share</TableCell>
                <TableCell>Sold Date</TableCell>
                <TableCell>Settlement Date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.map((p) => (
                <TableRow key={p.propertyId}>
                  <TableCell>
                    {agencySlug ? (
                      <Link
                        to={`/agencies/${agencySlug}/properties/${p.propertyId}`}
                        style={{ color: 'inherit', fontWeight: 500 }}
                      >
                        {p.propertyAddress}
                      </Link>
                    ) : (
                      p.propertyAddress
                    )}
                  </TableCell>
                  <TableCell align="right">
                    ${p.soldPrice.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell align="right">
                    ${p.commission.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium" color="success.main">
                      ${p.shareAmount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>{new Date(p.soldDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}</TableCell>
                  <TableCell>
                    {p.settlementDate
                      ? new Date(p.settlementDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={statusLabel(p.status ?? 'pending')}
                      color={statusColor((p.status ?? 'pending') as CommissionStatus)}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

/**
 * Commission Report � logged-in user's own commission performance.
 * Available to all users in the agency portal.
 */
const CommissionReport: React.FC = () => {
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const navigate = useNavigate();
  const { user, agency } = useAuth();
  const planName = (agency?.subscription?.planName || agency?.subscriptionPlan || 'Free').toLowerCase();
  const hasCommissionAccess = ['professional', 'agency premium', 'premium', 'enterprise', 'demo'].includes(planName);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [performance, setPerformance] = useState<AgentCommissionPerformance | null>(null);
  const [timelineYear, setTimelineYear] = useState(() => new Date().getFullYear());
  const [settlementFilter, setSettlementFilter] = useState<'all' | 'settlement_set'>('all');

  const userId = user?.id ?? (user as any)?._id;

  const loadMyCommission = useCallback(async () => {
    if (!agencySlug || !userId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await getAgentCommissionPerformance(agencySlug, userId);
      setPerformance(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load commission report');
    } finally {
      setLoading(false);
    }
  }, [agencySlug, userId]);

  useEffect(() => {
    if (agencySlug && userId) {
      loadMyCommission();
    }
  }, [agencySlug, userId, loadMyCommission]);

  const { pipeline, paid, cancelled, filteredProperties } = useMemo(() => {
    const list = performance?.properties ?? [];
    const withSettlementFilter =
      settlementFilter === 'settlement_set'
        ? list.filter((p) => p.settlementDate && String(p.settlementDate).trim() !== '')
        : list;
    const pipeline = withSettlementFilter.filter((p) => (p.status ?? 'pending') === 'pending');
    const paid = withSettlementFilter.filter((p) => p.status === 'paid');
    const cancelled = withSettlementFilter.filter((p) => p.status === 'cancelled');
    return { pipeline, paid, cancelled, filteredProperties: withSettlementFilter };
  }, [performance?.properties, settlementFilter]);

  const pipelineTotal = useMemo(
    () => pipeline.reduce((sum, p) => sum + p.shareAmount, 0),
    [pipeline]
  );
  const paidTotal = useMemo(
    () => paid.reduce((sum, p) => sum + p.shareAmount, 0),
    [paid]
  );
  const cancelledTotal = useMemo(
    () => cancelled.reduce((sum, p) => sum + p.shareAmount, 0),
    [cancelled]
  );

  const handleExportCsv = useCallback(() => {
    const rows = buildExportRows(
      filteredProperties.map((p) => ({
        ...p,
        status: (p.status ?? 'pending') as string,
        agentNames: undefined,
      })),
      'excluding_cancelled'
    );
    const base = `my-commission-report-${new Date().toISOString().slice(0, 10)}`;
    exportCommissionToCsv(rows, `${base}.csv`);
  }, [filteredProperties]);

  const handleTimelinePropertyClick = useCallback(
    (propertyId: string) => {
      if (agencySlug) {
        navigate(`/agencies/${agencySlug}/properties/${propertyId}`);
      }
    },
    [agencySlug, navigate]
  );

  if (!user || !userId) {
    return (
      <Box p={3}>
        <Alert severity="info">You must be logged in to view your commission report.</Alert>
      </Box>
    );
  }

  if (!hasCommissionAccess) {
    return (
      <Box p={3}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              My Commission Report
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Commission reports are available on the Agency Premium plan. Upgrade your subscription
              to view your commission performance.
            </Typography>
            <Button
              variant="contained"
              onClick={() => agencySlug && navigate(`/agencies/${agencySlug}/subscription`)}
            >
              View subscription
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
        <Typography variant="h4">My Commission Report</Typography>
        {performance && performance.properties.length > 0 && (
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="settlement-filter-label">Show</InputLabel>
              <Select
                labelId="settlement-filter-label"
                value={settlementFilter}
                label="Show"
                onChange={(e) => setSettlementFilter(e.target.value as 'all' | 'settlement_set')}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="settlement_set">Settlement date set</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Commission is paid at settlement. Reports may include pre-settlement calculations. Use the{' '}
        <strong>timeline</strong> to see commissions by month; navigate years with the arrows.{' '}
        <strong>In pipeline</strong> = all unpaid; <strong>Paid</strong> = commission paid.
      </Alert>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : performance ? (
        <Box>
          <CommissionTimeline
            properties={filteredProperties}
            selectedYear={timelineYear}
            onYearChange={setTimelineYear}
            onPropertyClick={agencySlug ? handleTimelinePropertyClick : undefined}
          />

          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Pipeline view — commissions in progress vs. paid
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <ScheduleIcon fontSize="small" />
                    <Typography variant="subtitle2">In pipeline</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold">
                    ${pipelineTotal.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {pipeline.length} {pipeline.length === 1 ? 'property' : 'properties'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <CheckCircleIcon fontSize="small" />
                    <Typography variant="subtitle2">Paid</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold">
                    ${paidTotal.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {paid.length} {paid.length === 1 ? 'property' : 'properties'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Total commission
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    ${performance.totalCommission.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {performance.propertyCount} properties (in pipeline + paid
                    {cancelled.length > 0 ? ' + cancelled' : ''})
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {cancelled.length > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ borderColor: 'action.disabled' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <CancelIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Cancelled
                      </Typography>
                    </Box>
                    <Typography variant="h6" color="text.secondary">
                      ${cancelledTotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {cancelled.length} {cancelled.length === 1 ? 'property' : 'properties'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {performance.properties.length > 0 ? (
            <Box>
              <PipelineTable
                title="In pipeline"
                subtitle="Settled, commission pending"
                icon={<ScheduleIcon />}
                color="warning"
                properties={pipeline}
                agencySlug={agencySlug}
              />
              <PipelineTable
                title="Paid"
                subtitle="Commission paid"
                icon={<CheckCircleIcon />}
                color="success"
                properties={paid}
                agencySlug={agencySlug}
              />
              {cancelled.length > 0 && (
              <PipelineTable
                title="Cancelled"
                subtitle="Rare: use only when sale did not proceed (e.g. settlement did not complete)"
                icon={<CancelIcon />}
                  color="default"
                  properties={cancelled}
                  agencySlug={agencySlug}
                />
              )}
            </Box>
          ) : (
            <Alert severity="info">
              No commission data. Ensure commission is calculated for your properties (sold price,
              commission rate, assigned agents).
            </Alert>
          )}
        </Box>
      ) : (
        <Alert severity="info">
          No commission data available for the selected period.
        </Alert>
      )}
    </Box>
  );
};

export default CommissionReport;
